import type { Theme } from '@mui/material';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import DashboardCard from '/@/components/bayer/dashboard-card';
import Tabs from '/@/components/bayer/tabs';
import { useCatalogProductById } from '/@/hooks/catalog';
import {
    type UserNodeFieldsMostRecommendedProductsResponse,
    useFieldsMostRecommendedProducts,
} from '/@/hooks/dashboards';
import { _ } from '/@/locale';
import { objectEntries } from '/@/utils';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import GenericDekalbBagImage from './assets/Bolsa Generica Dekalb.png';

const ProductCard = ({
    index,
    product,
}: {
    index: number;
    product: UserNodeFieldsMostRecommendedProductsResponse['mostRecommendedProducts'][number];
}) => {
    const isCropProtection = !['corn', 'soy'].includes(product.type);
    const productQuery = useCatalogProductById({
        id: product.productProfileId || product.cropProtectionProductId,
        crop: isCropProtection ? 'crop_protection' : product.type,
    });

    let brandImage =
        productQuery.data?.image ||
        productQuery.data?.technologies
            ?.find((tech) => tech.displayInCatalog)
            ?.logos?.find((logo) => logo.brandId === productQuery.data?.brand?.id)?.image;
    if ((!brandImage && product.type === 'corn') || product.type === 'soy') {
        brandImage = GenericDekalbBagImage;
    }

    return (
        <Stack
            p={2}
            justifyContent='space-between'
            alignItems='center'
            sx={{
                borderRadius: '8px',
                border: (t: Theme) => `1px solid ${t.palette.divider}`,
                background: '#F5F5F5',
            }}
            spacing={1.5}
            width='100%'
            minWidth='200px'
        >
            {brandImage ? (
                <Box component='img' src={brandImage} alt={productQuery.data?.name} height='100px' />
            ) : (
                <Skeleton variant='rounded' width='100px' height='100px' />
            )}
            <Stack
                sx={{
                    width: '32px',
                    padding: '8px',
                    borderRadius: '100px',
                    background: (theme: Theme) => theme.palette.primary?.onSurface,
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography variant='caption' fontWeight='bold' sx={{ color: 'white !important' }}>
                    {index}
                </Typography>
            </Stack>
            <Stack spacing={0.5} alignItems='center'>
                <Typography variant='h6' color='primary'>
                    {product.name}
                </Typography>
                <Typography variant='subtitle1' color='primary'>
                    {_('{{count}} Lote', { count: product.count })}
                </Typography>
            </Stack>
        </Stack>
    );
};

const MostRecommendedProductsDashboard = ({ userNodeId, year }: { userNodeId: number; year: number }) => {
    const [selectedTab, setSelectedTab] = useState<'hybrids' | 'protection' | 'glyphosate'>('hybrids');
    const tabs = {
        hybrids: {
            label: _('Híbridos'),
            filters: {
                type_in: ['corn', 'soy', 'corn_density'],
            },
        },
        protection: {
            label: _('Protección de cultivos'),
            filters: {
                type_in: ['weed', 'disease', 'plague'],
                has_glifosato: false,
            },
        },
        glyphosate: {
            label: _('Glifosato'),
            filters: {
                type_in: ['weed', 'disease', 'plague'],
                has_glifosato: true,
            },
        },
    };

    const mostRecommendedProductsQuery = useFieldsMostRecommendedProducts({
        node: userNodeId,
        year,
        ...getNodeScopeFilters('rvs'),
        ...tabs[selectedTab].filters,
    });

    const mostRecommendedProducts = mostRecommendedProductsQuery.data?.mostRecommendedProducts || [];

    return (
        <DashboardCard title={_('Productos más recomendados')}>
            <Stack spacing={3}>
                <Tabs
                    options={objectEntries(tabs).map(([key, tab]) => ({
                        label: tab.label,
                        value: key,
                    }))}
                    value={selectedTab}
                    setValue={(value) => setSelectedTab(value as 'hybrids' | 'protection' | 'glyphosate')}
                    variant='chips'
                />
                <Stack spacing={3} direction='row' overflow='auto'>
                    {mostRecommendedProductsQuery.isLoading ? (
                        Array.from({ length: 5 }).map((_, index) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: Its only for the skeleton
                            <Skeleton key={index} variant='rounded' width='250px' height='250px' />
                        ))
                    ) : mostRecommendedProducts.length > 0 ? (
                        mostRecommendedProducts
                            .slice(0, 5)
                            .map((product, index) => (
                                <ProductCard key={product.name} index={index + 1} product={product} />
                            ))
                    ) : (
                        <Stack
                            direction='column'
                            alignItems='center'
                            justifyContent='center'
                            width='100%'
                            spacing={'0.5'}
                        >
                            <Typography variant='body1' color='primary' fontWeight='bold'>
                                {_('Aún no hay productos recomendados en esta campaña')}
                            </Typography>
                            <Typography variant='body1' color='primary'>
                                {_('Cuando se generen nuevas recomendaciones, las vas a poder ver acá.')}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </DashboardCard>
    );
};

export default MostRecommendedProductsDashboard;
